import React, { useEffect, useState, Suspense } from "react";
import userAPI from "../../api/UserApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { useNavigate } from "react-router-dom";
import { setAccessToken } from "../../api/axiosInstance";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Mail, Lock, User, ArrowRight, Sparkles, Star, Gift } from "lucide-react";

function RegisterCanvas() {
  useAlphabeticInput();

  const INITIAL_FORM_DATA = {
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    confirmPassword: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);
  const isValidPassword = formData.password.length >= 6;

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName) {
      newErrors.firstName = "Please Enter First Name";
    }

    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Enter Password";
    } else if (!isValidPassword) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Enter Confirm Password";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.general = "Password & Confirm Password must be same";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const res = await userAPI.register(formData);
      if (res.code === 200) {
        showSuccess(res.message);
        setAccessToken(res.data.access_token);

        const user = {
          email: res.data.email,
          username: res.data.username,
        };

        localStorage.setItem("token", JSON.stringify(user));
        localStorage.setItem("accessToken", res.data.accessToken);

        navigate("/users/dashboard");
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    }
  };

  return (
    <>
      <div className="orangeapes-login-page-container">
        <Container fluid className="h-100 p-0">
          <Row className="h-100 g-0">
            
            {/* 🌟 LEFT SIDE: TEXT-BASED EDITORIAL MANIFESTO PANEL (IMAGE-FREE NO BOX) */}
            <Col lg={6} className="d-none d-lg-block orangeapes-login-text-manifesto-panel">
              <div className="manifesto-content-box">
                <div className="manifesto-top-branding">
                  <span>EST. 2025</span>
                  <h2>ORANGEAPES</h2>
                </div>

                <div className="manifesto-middle-perks-grid">
                  <div className="perk-text-row d-flex gap-3 align-items-start mb-4">
                    <Sparkles size={20} className="perk-icon-accent mt-1" />
                    <div>
                      <h6>Insiders Studio Access</h6>
                      <p>Create an account to gain early tracking access to upcoming premium lookbooks, signature jewelry drops, and capsule dress drops before they launch public.</p>
                    </div>
                  </div>

                  <div className="perk-text-row d-flex gap-3 align-items-start mb-4">
                    <Star size={20} className="perk-icon-accent mt-1" />
                    <div>
                      <h6>Curated Custom History</h6>
                      <p>Track your ongoing shipping logistics pipelines, update custom boutique sizing specifications, and safely maintain a centralized minimalist wishlist vault matrix.</p>
                    </div>
                  </div>

                  <div className="perk-text-row d-flex gap-3 align-items-start">
                    <Gift size={20} className="perk-icon-accent mt-1" />
                    <div>
                      <h6>Private Tier Rewards</h6>
                      <p>Registered club profiles automatically accumulate points to redeem seasonal complimentary concierge credits, birthday drops, and zero-tax checkout rules.</p>
                    </div>
                  </div>
                </div>

                <div className="manifesto-footer-copy">
                  <p>&copy; 2026 Orangeapes Studio. Engineered with precise structural minimalism.</p>
                </div>
              </div>
            </Col>

            {/* RIGHT SIDE: MINIMALIST REGISTRATION FORM CARD */}
            <Col
              lg={6}
              className="d-flex justify-content-center align-items-center p-4 p-md-5 bg-white"
            >
              <div className="orangeapes-login-form-card">
                
                <div className="form-header-typography mb-4">
                  <h1 className="orangeapes-login-main-title mb-2">Create Account</h1>
                  <p className="orangeapes-login-subtitle">
                    Register your signature profile to join the Orangeapes workspace stream.
                  </p>
                </div>

                <Form method="post" onSubmit={handleSubmit} className="orangeapes-form-element">
                  
                  {/* INLINE ROW: FIRST NAME & LAST NAME BLOCKS */}
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <Form.Group className="orangeapes-input-group-row" controlId="firstName">
                        <Form.Label className="orangeapes-form-custom-label">
                          First Name <span className="text-orange-token">*</span>
                        </Form.Label>
                        <div className="orangeapes-field-input-wrapper">
                          <User size={16} className="orangeapes-field-icon" />
                          <Form.Control
                            type="text"
                            name="firstName"
                            placeholder="First Name"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="orangeapes-blank-input"
                          />
                        </div>
                        {errors && errors.firstName && (
                          <span className="orangeapes-error-validation-msg">{errors.firstName}</span>
                        )}
                      </Form.Group>
                    </div>
                    
                    <div className="col-md-6">
                      <Form.Group className="orangeapes-input-group-row" controlId="lastName">
                        <Form.Label className="orangeapes-form-custom-label">
                          Last Name
                        </Form.Label>
                        <div className="orangeapes-field-input-wrapper">
                          <User size={16} className="orangeapes-field-icon" />
                          <Form.Control
                            type="text"
                            name="lastName"
                            placeholder="Last Name"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="orangeapes-blank-input"
                          />
                        </div>
                        {errors && errors.lastName && (
                          <span className="orangeapes-error-validation-msg">{errors.lastName}</span>
                        )}
                      </Form.Group>
                    </div>
                  </div>

                  {/* EMAIL ADDRESS BLOCK */}
                  <Form.Group className="orangeapes-input-group-row mb-4" controlId="email">
                    <Form.Label className="orangeapes-form-custom-label">
                      Email Address <span className="text-orange-token">*</span>
                    </Form.Label>
                    <div className="orangeapes-field-input-wrapper">
                      <Mail size={16} className="orangeapes-field-icon" />
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="name@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="orangeapes-blank-input"
                      />
                    </div>
                    {errors && errors.email && (
                      <span className="orangeapes-error-validation-msg">{errors.email}</span>
                    )}
                  </Form.Group>

                  {/* INLINE ROW: PASSWORD & CONFIRM PASSWORD BLOCKS */}
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <Form.Group className="orangeapes-input-group-row" controlId="password">
                        <Form.Label className="orangeapes-form-custom-label">
                          Password <span className="text-orange-token">*</span>
                        </Form.Label>
                        <div className="orangeapes-field-input-wrapper">
                          <Lock size={16} className="orangeapes-field-icon" />
                          <Form.Control
                            type="password"
                            name="password"
                            placeholder="••••••••"
                            value={formData.password}
                            onChange={handleChange}
                            className="orangeapes-blank-input"
                          />
                        </div>
                        {errors && errors.password && (
                          <span className="orangeapes-error-validation-msg">{errors.password}</span>
                        )}
                      </Form.Group>
                    </div>

                    <div className="col-md-6">
                      <Form.Group className="orangeapes-input-group-row" controlId="confirmPassword">
                        <Form.Label className="orangeapes-form-custom-label">
                          Confirm Password <span className="text-orange-token">*</span>
                        </Form.Label>
                        <div className="orangeapes-field-input-wrapper">
                          <Lock size={16} className="orangeapes-field-icon" />
                          <Form.Control
                            type="password"
                            name="confirmPassword"
                            placeholder="••••••••"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className="orangeapes-blank-input"
                          />
                        </div>
                        {errors && errors.confirmPassword && (
                          <span className="orangeapes-error-validation-msg">{errors.confirmPassword}</span>
                        )}
                      </Form.Group>
                    </div>
                  </div>

                  {/* GENERAL PASSWORD MATCH TRACK ERROR EXCEPTION DISPLAY */}
                  {errors.general && (
                    <div className="mb-4">
                      <span className="orangeapes-error-validation-msg text-center fw-bold">{errors.general}</span>
                    </div>
                  )}

                  {/* PREMIUM BUTTON TRIGGER SUBMIT */}
                  <Button
                    className="orangeapes-login-submit-btn-solid w-100 py-3 d-flex align-items-center justify-content-center gap-2 mt-2"
                    type="submit"
                  >
                    <span>CREATE PROFILE REGISTRY</span>
                    <ArrowRight size={14} className="submit-btn-arrow" />
                  </Button>

                  {/* REDIRECT ANCHOR FOOTER TERMINAL */}
                  <div className="text-center mt-5 pt-3 orangeapes-form-footer-border">
                    <p className="orangeapes-register-redirect-txt">
                      Already have an account?
                      <Link
                        to="/users/login"
                        className="orangeapes-bold-redirect-anchor ms-2"
                      >
                        Login Directly
                      </Link>
                    </p>
                  </div>

                </Form>
              </div>
            </Col>

          </Row>
        </Container>
      </div>
    </>
  );
}

export default RegisterCanvas;